/**
 * KAN-980: выдача аккаунтов пула кабинету: кабинет (кроме пула), его оператор,
 * отправка. На 409 со свежими аккаунтами показывает предупреждение и кнопку
 * "Всё равно выдать" (повтор с confirm_fresh).
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  useAssignPlatformTgAccounts,
  usePlatformOperators,
  usePlatformOrganizations,
} from '@/hooks/usePlatform';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { accountTitle, formatDateTime, parseFreshConflict } from '@/lib/platform';
import type { TransferResponse } from '@/services/api/platform';

interface AssignDialogProps {
  open: boolean;
  accountIds: string[];
  onClose: () => void;
  onDone: (result: TransferResponse) => void;
}

export function AssignDialog({ open, accountIds, onClose, onDone }: AssignDialogProps) {
  const [orgId, setOrgId] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const organizations = usePlatformOrganizations();
  const operators = usePlatformOperators(orgId || null);
  const assign = useAssignPlatformTgAccounts();

  const conflict = assign.isError ? parseFreshConflict(assign.error) : null;
  const error =
    assign.isError && !conflict ? extractErrorMessage(assign.error, 'Не удалось выдать аккаунты') : null;

  const close = () => {
    setOrgId('');
    setOperatorId('');
    assign.reset();
    onClose();
  };

  const send = (confirmFresh: boolean) => {
    const payload = confirmFresh
      ? { account_ids: accountIds, target_operator_id: operatorId, confirm_fresh: true }
      : { account_ids: accountIds, target_operator_id: operatorId };
    assign.mutate(payload, {
      onSuccess: (result) => {
        close();
        onDone(result);
      },
    });
  };

  const orgOptions = (organizations.data ?? [])
    .filter((org) => !org.is_pool)
    .map((org) => ({ value: org.id, label: org.name }));
  const operatorOptions = (operators.data ?? []).map((op) => ({
    value: op.id,
    label: op.name ? `${op.email} (${op.name})` : op.email,
  }));
  const canSubmit = Boolean(orgId && operatorId && accountIds.length > 0) && !assign.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Выдать кабинету</DialogTitle>
          <DialogDescription>Аккаунтов выбрано: {accountIds.length}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="platform-assign-org">Кабинет</Label>
            <NativeSelect
              id="platform-assign-org"
              data-testid="platform-assign-org"
              value={orgId}
              placeholder="Выберите кабинет"
              options={orgOptions}
              onChange={(e) => {
                setOrgId(e.target.value);
                setOperatorId('');
                assign.reset();
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="platform-assign-operator">Оператор</Label>
            <NativeSelect
              id="platform-assign-operator"
              data-testid="platform-assign-operator"
              value={operatorId}
              placeholder={orgId && operators.data?.length === 0 ? 'Нет операторов с Telegram' : 'Выберите оператора'}
              options={operatorOptions}
              disabled={!orgId}
              onChange={(e) => {
                setOperatorId(e.target.value);
                assign.reset();
              }}
            />
          </div>
          {conflict && (
            <div
              data-testid="platform-assign-fresh-warning"
              role="alert"
              className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
            >
              <p>{conflict.detail}</p>
              <ul className="list-disc pl-5">
                {conflict.fresh.map((acc) => (
                  <li key={acc.account_id}>
                    {accountTitle(acc)}, подключён {formatDateTime(acc.connected_at)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {error && (
            <p data-testid="platform-assign-error" role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Отмена
          </Button>
          {conflict ? (
            <Button
              type="button"
              variant="destructive"
              data-testid="platform-assign-confirm"
              disabled={assign.isPending}
              onClick={() => send(true)}
            >
              Всё равно выдать
            </Button>
          ) : (
            <Button type="button" data-testid="platform-assign-submit" disabled={!canSubmit} onClick={() => send(false)}>
              {assign.isPending ? 'Выдаю...' : 'Выдать'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
